import { useEffect, useMemo } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Site, ProductsData, Workspace } from './types.tsx';

const AVAILABLE_SITES_ENDPOINT = '/gateway/api/available-sites';
const AVAILABLE_SITES_ENDPOINT_NEW = '/gateway/api/v2/accessible-products';

export const addJPDSitesInOptions = (): string[] => ['jira-product-discovery'];

const fetchJiraAppLinkOld = async () =>
	performPostRequest(AVAILABLE_SITES_ENDPOINT, {
		body: JSON.stringify({
			products: [
				'jira-software.ondemand',
				'jira-core.ondemand',
				'jira-servicedesk.ondemand',
				...addJPDSitesInOptions(),
			],
		}),
	});

const fetchJiraAppLinkNew = async () =>
	performPostRequest(AVAILABLE_SITES_ENDPOINT_NEW, {
		body: JSON.stringify({
			productIds: [
				'jira-software.ondemand',
				'jira-core.ondemand',
				'jira-servicedesk.ondemand',
				...addJPDSitesInOptions(),
			],
		}),
	});

export const transformJiraAppSites = (item: Site) => ({
	label: item.displayName,
	value: item.cloudId,
	url: item.url,
});

export const transformJiraAppSitesForSelect = (sites: Site[]) => sites.map(transformJiraAppSites);

const transformUrlToDisplayName = (url: string) => {
	// Remove protocol (http:// or https://) from the URL
	return url.replace(/^https?:\/\//, '');
};

export const mergeAndTransformSiteData = (productsData: ProductsData) => {
	if (!productsData?.data?.products) {
		return [];
	}

	const allWorkspaces: Workspace[] = [];

	productsData.data.products.forEach((product) => {
		if (product.workspaces && Array.isArray(product.workspaces)) {
			allWorkspaces.push(...product.workspaces);
		}
	});

	const transformedWorkspaces = allWorkspaces.map((workspace) => ({
		label: transformUrlToDisplayName(workspace.workspaceUrl),
		value: workspace.cloudId,
		url: workspace.cloudUrl,
	}));

	const uniqueWorkspaces = transformedWorkspaces.filter(
		(workspace, index, array) => array.findIndex((w) => w.url === workspace.url) === index,
	);

	return uniqueWorkspaces;
};

const useFetchJiraAvailableSites = () => {
	const fetchJiraAppLink = fg('site_picker_api_change') ? fetchJiraAppLinkNew : fetchJiraAppLinkOld;
	const { loading, error, data, fetch } = useService(fetchJiraAppLink);

	useEffect(() => {
		fetch();
	}, [fetch]);

	const sites = useMemo(() => {
		if (data && data.sites && data.sites.length && !fg('site_picker_api_change'))
			return transformJiraAppSitesForSelect(data.sites);
		if (fg('site_picker_api_change')) {
			const mergedTransformedSiteData = mergeAndTransformSiteData(data);
			return mergedTransformedSiteData;
		}
		return [];
	}, [data]);

	return {
		isLoading: loading,
		error,
		sites,
	};
};

export default useFetchJiraAvailableSites;
